"use client";

import { useContext } from "react";
import DataContext from "../Context/DAtaContext";
import UsersContext from "../Context/UsersContext";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { motion } from "framer-motion";
import { TrendingUp } from "lucide-react";
import { GrMoney } from "react-icons/gr";

export const StatCard = ({
  title,
  value,
  icon: Icon,
  color,
  trend,
  subtitle,
  itemVariants,
}) => (
  <motion.div
    variants={itemVariants}
    className="p-6 transition-shadow bg-white border border-gray-200 shadow-lg dark:bg-gray-800 rounded-xl dark:border-gray-700 hover:shadow-xl"
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600 capitalize dark:text-gray-400">
          {title}
        </p>
        <motion.p
          initial={{ scale: 0.5 }}
          animate={{ scale: 1 }}
          className="mt-2 text-2xl font-bold text-gray-900 dark:text-white"
        >
          {value}
        </motion.p>
        {subtitle && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {subtitle}
          </p>
        )}
        {trend && (
          <p className="flex items-center mt-1 text-sm text-green-600 dark:text-green-400">
            <TrendingUp className="w-4 h-4 mr-1" />
            {trend}
          </p>
        )}
      </div>
      <div className={`p-3 dark:bg-gray-900/60 rounded-lg ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </motion.div>
);

export default function InvestmentByGender() {
  const { Payment } = useContext(DataContext);
  const { farmers } = useContext(UsersContext);

  // Group farmers by gender and sum their payments
  const genderInvestment = {};
  const genderCount = {};

  farmers?.forEach((farmer) => {
    const farmerPayments = Payment?.filter((p) => p.farmer_id === farmer.uuid);

    const totalInvestment = farmerPayments?.reduce(
      (sum, p) => sum + p.amount,
      0
    );

    if (totalInvestment > 0) {
      const gender = farmer.gender || "Unknown";
      if (!genderInvestment[gender]) {
        genderInvestment[gender] = 0;
        genderCount[gender] = 0;
      }
      genderInvestment[gender] += totalInvestment;
      genderCount[gender] += 1;
    }
  });

  const genderData = Object.entries(genderInvestment).map(
    ([gender, total]) => ({
      gender,
      total,
    })
  );

  //   Animation logic
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-TZ", {
      style: "currency",
      currency: "TZS",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  return (
    <div className="shadow-sm rounded-xl h-[450px]">
      <h2 className="mb-4 text-lg font-semibold text-gray-500 dark:text-gray-400">
        Total Investment per Gender
      </h2>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-4 ">
          {Object.entries(genderCount).map(([gender, count]) => (
            <StatCard
              itemVariants={itemVariants}
              key={gender}
              title={`${gender} Investors`}
              value={count}
              icon={GrMoney}
              color="bg-green-500"
              trend={formatCurrency(genderInvestment[gender] || 0)}
              subtitle={`Total: ${formatCurrency(
                genderInvestment[gender] || 0
              )}`}
            />
          ))}
        </div>

        {/* Chart */}
        <div className="">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={genderData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="gender" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="total" fill="#10B981" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
